import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@quake/shared-ui/components/ui/command";
import { Button } from "@quake/shared-ui/components/ui/button";
import BadgePlusIcon from "lucide-react/dist/esm/icons/badge-plus";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import BrainIcon from "lucide-react/dist/esm/icons/brain";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import type { PresetDetails, PresetSkillOption } from "./types";

export function SkillSetMembersEditor({
  preset,
  skillQuery,
  setSkillQuery,
  options,
  isLoading,
  isSaving,
  onAddSkill,
  onRemoveSkill,
}: {
  preset: PresetDetails;
  skillQuery: string;
  setSkillQuery: (value: string) => void;
  options: PresetSkillOption[];
  isLoading: boolean;
  isSaving: boolean;
  onAddSkill: (skillId: string) => Promise<unknown>;
  onRemoveSkill: (skillId: string) => Promise<unknown>;
}) {
  const selectedSkillIds = new Set(preset.members.map((member) => member.skill_id));
  const availableOptions = options.filter((option) => !selectedSkillIds.has(option.skill_id));

  return (
    <section className="rounded-xl border border-black/[0.07] bg-white p-6 dark:border-white/[0.08] dark:bg-white/[0.03]">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Included skills</h2>
        <div className="flex items-center gap-3">
          <p className="inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            {isSaving ? <Loader2Icon className="size-3 animate-spin" /> : null}
            {isSaving ? "Saving…" : "Auto-saves"}
          </p>
          <span className="text-xs tabular-nums text-slate-400 dark:text-slate-500">
            {preset.members.length} skill{preset.members.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)]">
        <div className="min-w-0">
          {preset.members.length === 0 ? (
            <div className="rounded-lg border border-dashed border-black/10 px-4 py-10 text-center text-sm text-slate-400 dark:border-white/10 dark:text-slate-500">
              No skills yet.
            </div>
          ) : (
            <div className="divide-y divide-black/[0.06] dark:divide-white/[0.06]">
              {preset.members.map((member) => (
                <div key={member.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <BrainIcon className="size-3.5 shrink-0 text-slate-400" />
                    <p className="truncate text-sm text-slate-800 dark:text-slate-200">{member.skill_name}</p>
                    <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                      ×{member.priority_weight ?? 1}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveSkill(member.skill_id)}
                    aria-label={`Remove ${member.skill_name}`}
                    disabled={isSaving}
                    className="size-7 shrink-0 text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
                  >
                    <Trash2Icon className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <Command className="rounded-lg border border-black/[0.07] dark:border-white/[0.08] h-96">
            <CommandInput
              value={skillQuery}
              onValueChange={setSkillQuery}
              placeholder="Search skills or aliases"
              disabled={isSaving}
            />
            <CommandList className="max-h-104">
              {isLoading && <div className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">Loading…</div>}
              {!isLoading && availableOptions.length === 0 && (
                <CommandEmpty>
                  {selectedSkillIds.size > 0 ? "No additional skills found." : "No skills found."}
                </CommandEmpty>
              )}
              <CommandGroup heading="Available skills">
                {availableOptions.map((option) => (
                  <CommandItem
                    key={option.skill_id}
                    value={`${option.label} ${option.description ?? ""}`}
                    onSelect={() => onAddSkill(option.skill_id)}
                    className="cursor-pointer"
                    disabled={isSaving}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{option.label}</p>
                      {option.description && (
                        <p className="truncate text-xs text-muted-foreground">{option.description}</p>
                      )}
                    </div>
                    {isSaving ? (
                      <Loader2Icon className="size-3.5 animate-spin text-slate-400" />
                    ) : (
                      <BadgePlusIcon className="size-3.5 text-slate-400" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </div>
    </section>
  );
}
